/**
 * Reality check with backward-compatible time parsing.
 * - Храним времена в localStorage строго как миллисекунды эпохи (stringified number).
 * - Поддерживаем старую версию, где даты сохранялись как UTC-строки (toUTCString()).
 * - При обнаружении старого формата сразу мигрируем к мс-эпохе.
 * - Если пропускали интервалы — выравниваемся на ближайшую будущую «границу».
 * - Если есть ключ `debug` в localStorage — включаем консольное логирование.
 *
 * @example
 * localStorage.setItem('sessionStartTime', Date.now().toString());
 * const cleanup = startRealityCheck(() => {
 *   console.log('Reality check at:', new Date());
 * }, 600);
 */

const LS_KEYS = {
  sessionStartTime: 'sessionStartTime',
  lastRealityCheck: 'lastRealityCheck',
  counter: 'reality-check-counter',
  debug: 'debug',
} as const;

type TimerId = ReturnType<typeof setTimeout>;
type IntervalId = ReturnType<typeof setInterval>;

const hasDebug = () => localStorage.getItem(LS_KEYS.debug) !== null;
const log = (...args: any[]) => {
  if (hasDebug()) console.log('[startRealityCheck]', ...args);
};

// Singleton scheduling: keep one active timer across the app
let globalTimeoutId: TimerId | null = null;
let globalIntervalId: IntervalId | null = null;
let runCounter = 0;

function clearGlobalTimers(reason?: string) {
  if (globalTimeoutId != null) {
    clearTimeout(globalTimeoutId);
    globalTimeoutId = null;
    log('cleared previous global timeout', reason ? `(${reason})` : '');
  }
  if (globalIntervalId != null) {
    clearInterval(globalIntervalId);
    globalIntervalId = null;
    log('cleared previous global interval', reason ? `(${reason})` : '');
  }
}

export function stopRealityCheck() {
  // Increment runCounter so any pending callbacks from older runs are ignored
  runCounter++;
  clearGlobalTimers('stopRealityCheck');
}

function readMsCompat(key: string): { ms: number | null; migrated: boolean } {
  const raw = localStorage.getItem(key);
  if (!raw) return { ms: null, migrated: false };

  const asNum = Number(raw);
  if (Number.isFinite(asNum)) return { ms: asNum, migrated: false };


  const parsed = Date.parse(raw);
  if (Number.isFinite(parsed)) {

    localStorage.setItem(key, String(parsed));
    log(`migrated "${key}" from legacy string → ${parsed}`);
    return { ms: parsed, migrated: true };
  }


  log(`invalid time in "${key}" →`, raw);
  return { ms: null, migrated: false };
}

function writeMs(key: string, ms: number) {
  localStorage.setItem(key, String(ms));
}

function incCounter() {
  const current = Number(localStorage.getItem(LS_KEYS.counter) || '0');
  const next = (Number.isFinite(current) ? current : 0) + 1;
  localStorage.setItem(LS_KEYS.counter, String(next));
}

export function startRealityCheck(
  callback: (() => void) | null,
  realityCheckSeconds: number
): () => void {
  // bump run id and clear any previously scheduled timers
  const runId = ++runCounter;
  clearGlobalTimers('new run');
  if (realityCheckSeconds < 0) {
    throw new Error('Reality check interval must be non-negative');
  }

  const intervalMs = realityCheckSeconds * 1000;
  // Speed up in debug: keep the same API, but reduce interval
  const effectiveIntervalMs = hasDebug() ? intervalMs / 10 : intervalMs;
  if (intervalMs === 0) {
    log('interval = 0 → single immediate execution');
    clearGlobalTimers('0s interval immediate');
    if (typeof callback === 'function') {
      try {
        writeMs(LS_KEYS.lastRealityCheck, Date.now());
        incCounter();
        callback();
      } catch (e) {
        console.error('startRealityCheck: callback failed (0s interval):', e);
      }
    }
    return () => null;
  }

  let { ms: sessionStart } = readMsCompat(LS_KEYS.sessionStartTime);
  if (sessionStart == null) {
    sessionStart = Date.now();
    writeMs(LS_KEYS.sessionStartTime, sessionStart);
    log('initialized sessionStartTime =', sessionStart, new Date(sessionStart).toUTCString());
  }

  const { ms: lastCheck } = readMsCompat(LS_KEYS.lastRealityCheck);

  const now = Date.now();
  const baseTime = lastCheck ?? sessionStart;
  const elapsed = now - baseTime;

  let triggerTime: number;
  if (elapsed < effectiveIntervalMs) {
    triggerTime = baseTime + effectiveIntervalMs;
  } else {
    const missed = Math.floor(elapsed / effectiveIntervalMs);
    triggerTime = baseTime + (missed + 1) * effectiveIntervalMs;
  }

  const delay = Math.max(0, triggerTime - now);

  log({
    sessionStart,
    lastCheck,
    now,
    intervalMs,
    effectiveIntervalMs,
    elapsed,
    triggerTime,
    delay,
    note:
      elapsed >= intervalMs
        ? 'missed intervals → aligned to next boundary'
        : 'scheduled to first boundary',
  });

  let timeoutId: TimerId | null = null;
  let intervalId: IntervalId | null = null;

  const executeCheck = () => {
    if (runId !== runCounter) {
      log('skip executeCheck due to newer run');
      return;
    }
    try {
      const ts = Date.now();
      writeMs(LS_KEYS.lastRealityCheck, ts);
      incCounter();
      log('executeCheck at', ts, new Date(ts).toUTCString());
      if (typeof callback === 'function') callback();
    } catch (error) {
      console.error('startRealityCheck: callback failed:', error);
    }
  };

  timeoutId = setTimeout(() => {
    if (runId !== runCounter) {
      log('initial timeout ignored due to newer run');
      return;
    }
    log('initial timeout fired');
    executeCheck();
    intervalId = setInterval(() => {
      if (runId !== runCounter) {
        log('interval tick ignored due to newer run');
        return;
      }
      log('interval tick');
      executeCheck();
    }, effectiveIntervalMs);
    // capture as global after creation
    globalIntervalId = intervalId;
  }, delay);
  // set global timeout as the active one
  globalTimeoutId = timeoutId;

  return () => {
    if (timeoutId != null) {
      clearTimeout(timeoutId);
      if (globalTimeoutId === timeoutId) globalTimeoutId = null;
      timeoutId = null;
      log('cleared initial timeout');
    }
    if (intervalId != null) {
      clearInterval(intervalId);
      if (globalIntervalId === intervalId) globalIntervalId = null;
      intervalId = null;
      log('cleared interval');
    }
  };
}
